// Rummy — top-level game page.
//
// Responsibilities:
//   • Lock orientation to landscape (native JS + CSS rotate prompt).
//   • Maintain phase state: lobby / queue / playing / result.
//   • Own the WebSocket and dispatch all server messages into local state.
//   • Compose lobby / waiting modal / game table based on phase.
//   • Wire voice chat (WSVoice instance per match).
//
// Per project rule: everything game-specific stays here in games/rummy/.
// No platform edits needed beyond the one registry line.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { setWalletBalance, useWallet } from '@baaligames/shared-ui/lib/wallet';
import Icon from '@baaligames/shared-ui/components/Icon';
import LobbyScreen from './LobbyScreen';
import WaitingModal from './WaitingModal';
import Seat from './Seat';
import SelfBar from './SelfBar';
import CenterTable from './CenterTable';
import HandStrip from './HandStrip';
// RotatePrompt was the rummy-local rotate overlay. Orientation enforcement
// now lives at the route layer (player-frontend/src/App.tsx → GameRoute)
// and uses the shared shared-ui/components/OrientationPrompt — declaring
// `orientation: 'landscape'` in rummy/index.ts is the entire contract.
// Import removed; the file is still on disk for now and can be deleted
// once we confirm nothing else depends on it.
// import RotatePrompt from './RotatePrompt';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';
import LobbyBanner from '@baaligames/shared-ui/components/LobbyBanner';
import ConcurrentMatchModal from '@baaligames/shared-ui/components/ConcurrentMatchModal';
import GameLoadingScreen from '@baaligames/shared-ui/components/GameLoadingScreen';
import { useAssetPreloader } from '@baaligames/shared-ui/lib/useAssetPreloader';
import { isSessionAcked, markSessionAcked, clearSessionAcked } from '@baaligames/shared-ui/lib/recoveryAck';
import RecoveryScreen, { type RummyRecoveryKind } from './RecoveryScreen';
import VsIntro from './VsIntro';
import DealAnimation, { type DealTarget } from './DealAnimation';
import { RummyWS } from './ws';
import { WSVoice } from './wsVoice';
import {
  primeAudio, setSfxEnabled,
  playShuffle, playDeal, playDrawClosed, playDrawOpen, playDiscard,
  playWin, playInvalid, playChipsClink,
  playTap, playYourTurnChime, playCancelSound, playLossTone,
  startUrgencyBeep, stopUrgencyBeep,
} from './sounds';
import type { CardData } from './Card';
import './rummy.css';

type Phase = 'lobby' | 'queue' | 'playing' | 'result';

type Profile = {
  seat?: string;
  displayName?: string;
  avatarUrl?: string | null;
  isBot?: boolean;
  /** Bot difficulty from `bots.personality_type` (real DB bot) or
   *  the bracket fallback (synthetic bot). Rendered as the
   *  EASY/MEDIUM/HARD tag in the opponent's Seat. Mirrors the
   *  Carrom HUD treatment. null for humans. */
  botMode?: string | null;
  wallet?: number;
};

type LobbyConfig = {
  audioEnabled: boolean;
  platformFeePercent: number;
  betTiers: number[];
  playerModes: number[];
  turnSeconds: number;
  firstDropPenalty: number;
  middleDropPenalty: number;
  // Server-driven penalty/safety values now exposed via /api/rummy/config
  // so admin edits propagate without redeploy. UI surfaces that warn
  // about these values (invalid-declare confirmation, max-loss banner)
  // can read directly from `config?.invalidDeclarePenalty` etc.
  invalidDeclarePenalty?: number;
  maxLossCap?: number;
  decksPerMatch?: number;
  disconnectGraceSeconds?: number;
  urgencyBeepLeadSeconds?: number;
  humanStrikeLimit?: number;
  variant: string;
  lobbyBanner?: { enabled: boolean; text: string; tone: 'info' | 'warning' | 'critical' };
};

type WaitingState = {
  current: number;
  required: number;
  profiles: { userId?: string; name?: string; avatarUrl?: string | null; is_bot?: boolean }[];
  yourUserId: string | null;
  botActivateAt: number | null;
};

type Result =
  | { kind: 'win'; payout: number; pot: number }
  | { kind: 'loss'; penalty: number; pot: number }
  | { kind: 'drop_first' | 'drop_middle'; penalty: number }
  | { kind: 'kick'; penalty: number }
  | { kind: 'invalid_declare'; penalty: number; reason?: string }
  | { kind: 'aborted' };

function seatPositionForIndex(totalSeats: number, relIdx: number) {
  // Arrange opponents around the rim. relIdx 0 = immediately clockwise
  // from the player; 1 = next, etc. Player is at the bottom.
  // Layouts hard-coded per player-count for now.
  if (totalSeats === 2) {
    return { top: '6%', left: '50%', transform: 'translateX(-50%)', side: 'top' as const };
  }
  if (totalSeats === 4) {
    if (relIdx === 0) return { top: '25%', left: '6%', side: 'left' as const };
    if (relIdx === 1) return { top: '6%', left: '50%', transform: 'translateX(-50%)', side: 'top' as const };
    return { top: '25%', right: '6%', side: 'right' as const };
  }
  // 6 players — 5 opponents perimeter.
  if (relIdx === 0) return { top: '45%', left: '2%', side: 'left' as const };
  if (relIdx === 1) return { top: '18%', left: '16%', side: 'left' as const };
  if (relIdx === 2) return { top: '6%',  left: '50%', transform: 'translateX(-50%)', side: 'top' as const };
  if (relIdx === 3) return { top: '18%', right: '16%', side: 'right' as const };
  return           { top: '45%', right: '2%', side: 'right' as const };
}

// Error codes the WS / engine layers handle internally — don't
// surface them as user-facing toasts (the recovery is silent).
const RUMMY_TRANSIENT_ERROR_CODES = new Set([
  'auth_failed',
  'not_authed',     // post-restart frame queued before re-auth — WS
                    // class auto-recovers; toast cascade was masking
                    // real freezes during pm2 restarts.
  'unknown_type',
]);

// Stable preload list for the Rummy lobby + match screen. Module-
// scoped so identity is fixed across renders.
const RUMMY_PRELOAD_URLS: string[] = [
  '/games/rummy/Logo.png',
  '/games/rummy/Coin.png',
  '/games/rummy/Plus.png',
  '/games/rummy/Settings.png',
  '/games/rummy/Cash-1.png',
  '/games/rummy/Cash-2.png',
  '/games/rummy/Cash-3.png',
  '/games/rummy/Cash-4.png',
  '/games/rummy/Card-2.png',
  '/games/rummy/Card-4.png',
  '/games/rummy/Card-6.png',
  '/games/rummy/Victory_Trophy.png',
  '/games/rummy/Defeat_Medal.png',
];

export default function Rummy() {
  const { user } = useAuth();
  // Bootstrap gate — see matching block in Ludo / XO / Chess / Carrom.
  // Loading overlay covers the route until BOTH `authReady` AND every
  // preloaded asset URL have settled.
  const assetStatus = useAssetPreloader(RUMMY_PRELOAD_URLS, 600);
  const [authReady, setAuthReady] = useState(false);
  const { balance } = useWallet();
  const toast = useToast();
  const nav = useNavigate();
  // Orientation enforcement moved to the route layer (App.tsx →
  // GameRoute). Rummy's GameModule declares `orientation: 'landscape'`;
  // shared-ui/lib/useGameOrientationLock handles the hardware lock and
  // shared-ui/components/OrientationPrompt renders the rotate overlay.
  // Nothing in this component needs to know about device orientation
  // any more.

  const [config, setConfig] = useState<LobbyConfig | null>(null);
  const [phase, setPhase] = useState<Phase>('lobby');
  const phaseRef = useRef<Phase>('lobby');
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const [online, setOnline] = useState(0);
  const [waitingByBet, setWaitingByBet] = useState<Record<string, number>>({});
  const [showWaitingBadge, setShowWaitingBadge] = useState(false);

  const [selectedPlayers, setSelectedPlayers] = useState<2 | 4 | 6>(2);
  const [selectedBet, setSelectedBet] = useState<number | null>(null);

  const [queueState, setQueueState] = useState<WaitingState | null>(null);
  // Concurrent-match modal state.
  const [concBlocking, setConcBlocking] = useState<any[] | null>(null);
  const [concOpen,     setConcOpen]     = useState(false);

  // In-match state.
  const [seats, setSeats] = useState<string[]>([]);
  // Refs mirror `seats` + `mySeat` so the stable WS handler closure
  // can still look up current seat positions for fly-card animations
  // without re-registering the listener on every render.
  const seatsRef = useRef<string[]>([]);
  useEffect(() => { seatsRef.current = seats; }, [seats]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [mySeat, setMySeat] = useState<string | null>(null);
  const mySeatRef = useRef<string | null>(null);
  useEffect(() => { mySeatRef.current = mySeat; }, [mySeat]);
  const [hand, setHand] = useState<CardData[]>([]);
  const [handGroups, setHandGroups] = useState<CardData[][]>([]);   // mirror for drag/drop
  const [topDiscard, setTopDiscard] = useState<CardData | null>(null);
  const [wildCard, setWildCard] = useState<CardData | null>(null);
  const [wildRank, setWildRank] = useState<string | null>(null);
  const [currentTurn, setCurrentTurn] = useState<string | null>(null);
  // Ref mirror of currentTurn so the WS message-handler closure (which
  // is captured at WS-mount time and never re-created) can still read
  // the latest value. Used to detect "turn just became MY turn" edges
  // for the your-turn chime without re-registering the handler on
  // every state change.
  const currentTurnRef = useRef<string | null>(null);
  useEffect(() => { currentTurnRef.current = currentTurn; }, [currentTurn]);
  // Bumps each turn rotation so SelfBar's timer-ring CSS animation
  // restarts cleanly (SVG is remounted via React `key`).
  const [turnSeq, setTurnSeq] = useState(0);

  // Urgency beep — fires the alternating two-tone cadence when the
  // turn timer enters its final `URGENCY_BEEP_LEAD_SECONDS` window.
  // Stops automatically at bar-end. Cosmetic only; the server-side
  // turn timeout is the authority. Parity with Ludo + X&O + Chess.
  // Uses turnSeq as the trigger so a fresh urgency window arms on
  // every new turn (including bonus turns and reconnect resyncs).
  useEffect(() => {
    stopUrgencyBeep();
    if (phase !== 'playing') return;
    const turnSec = Number(config?.turnSeconds || 30);
    const leadSec = Number(config?.urgencyBeepLeadSeconds ?? 4);
    if (leadSec <= 0 || turnSec <= leadSec) return;
    const URGENCY_START_MS = (turnSec - leadSec) * 1000;
    const BAR_END_MS       = turnSec * 1000;
    const startT = setTimeout(() => startUrgencyBeep(), URGENCY_START_MS);
    const stopT  = setTimeout(() => stopUrgencyBeep(),  BAR_END_MS);
    return () => {
      clearTimeout(startT);
      clearTimeout(stopT);
      stopUrgencyBeep();
    };
  }, [phase, currentTurn, turnSeq, config?.turnSeconds, config?.urgencyBeepLeadSeconds]);

  const [kicked, setKicked] = useState<Set<string>>(new Set());
  const [dropped, setDropped] = useState<Set<string>>(new Set());
  const [absent, setAbsent] = useState<Set<string>>(new Set());
  // Consecutive turn-timeouts per seat. Hits strike limit → auto-kick.
  // Backend broadcasts on `rummy_turn_timeout`; we also reset a seat
  // to 0 when they successfully draw a card (backend resets internally).
  const [strikes, setStrikes] = useState<Record<string, number>>({});
  // Pot value is tracked on match_start and surfaced in the top-bar
  // POT chip so players can see the stake at a glance — parity with
  // Ludo's top-pot-amt and X&O's match-payout chip.
  const [pot, setPot] = useState(0);
  const [result, setResult] = useState<Result | null>(null);

  // Drop-confirm modal state.
  const [dropConfirm, setDropConfirm] = useState(false);

  // Previous-game recovery popup (fetched from /api/rummy/pending-result).
  // Shape mirrors Ludo's recovery state: the outcome + amount-to-show
  // + sessionId used for the acknowledge POST.
  type RummyRecovery = {
    sessionId: number;
    kind: RummyRecoveryKind;
    amount: number;
    pot: number;
    platformFee?: number;
    opponents?: Array<{ name?: string }>;
  };
  const [recovery, setRecovery] = useState<RummyRecovery | null>(null);
  // In-game settings cog modal (sound / mic / listen / exit).
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Settings toggles (per project requirement).
  const [micMuted, setMicMuted] = useState(false);
  const [listenMuted, setListenMuted] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  useEffect(() => { setSfxEnabled(soundOn); }, [soundOn]);

  // VS intro + deal-animation overlays. Both play ONCE on match_start;
  // on reconnect_sync (re-entering an in-progress match) they're
  // skipped so the reconnecting player doesn't watch 5s of fluff.
  const [showVs,   setShowVs]   = useState(false);
  const [showDeal, setShowDeal] = useState(false);

  // Opponent draw / discard fly cards — a transient list of DOM
  // elements showing who just moved. Cleared after each animation via
  // onAnimationEnd. Capped so a glitchy burst of events can't pile up
  // thousands of DOM nodes.
  type FlyCard = { id: number; style: React.CSSProperties };
  const [flyCards, setFlyCards] = useState<FlyCard[]>([]);
  const flyIdRef = useRef(0);
  function spawnFly(posStyle: React.CSSProperties) {
    const id = ++flyIdRef.current;
    setFlyCards((xs) => [...xs.slice(-7), { id, style: posStyle }]);
    setTimeout(() => setFlyCards((xs) => xs.filter((x) => x.id !== id)), 650);
  }

  // Given an opponent seat colour, return the CSS offsets the fly-card
  // should appear at. Reads from the ref mirrors of seats + mySeat so
  // it works inside the stable WS-message closure.
  function seatFlyStyle(seat: string): React.CSSProperties | null {
    const totalSeats = seatsRef.current.length;
    if (!totalSeats) return null;
    const opps = seatsRef.current.filter((s) => s !== mySeatRef.current);
    const relIdx = opps.indexOf(seat);
    if (relIdx < 0) return null;
    const pos = seatPositionForIndex(totalSeats, relIdx) as any;
    const { side: _drop, ...rest } = pos;
    return rest as React.CSSProperties;
  }

  // WS + voice.
  const wsRef = useRef<RummyWS | null>(null);
  const voiceRef = useRef<WSVoice | null>(null);

  // Keep hand ↔ handGroups in sync when the server sends a fresh hand.
  useEffect(() => {
    if (handGroups.flat().length === 0 && hand.length > 0) {
      setHandGroups([hand]);
    }
  }, [hand, handGroups]);

  // Static config fetch.
  useEffect(() => {
    fetch('/api/rummy/config').then((r) => r.json()).then((j) => {
      if (j?.ok) {
        setConfig(j.data);
        if (selectedBet == null && j.data.betTiers?.length) {
          const nonZero = j.data.betTiers.filter((x: number) => x > 0);
          setSelectedBet(nonZero[0] ?? null);
        }
      }
    }).catch(() => {});
  }, []);

  // Previous-game recovery popup. Pulls the last unacknowledged
  // result from /api/rummy/pending-result on mount. If the local
  // `isSessionAcked` guard fires (user already dismissed on a prior
  // visit but the server missed the POST) we retry silently and
  // DON'T re-show. Mirrors Ludo + X&O exactly for UX parity.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch('/api/rummy/pending-result', { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const p = j?.data?.pending;
        if (!p || !p.sessionId) return;
        if (isSessionAcked('rummy', p.sessionId)) {
          fetch('/api/rummy/acknowledge-result', {
            method:  'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ sessionId: p.sessionId }),
          })
            .then(() => clearSessionAcked('rummy', p.sessionId))
            .catch(() => {});
          return;
        }
        // Map backend result → our RummyRecoveryKind + amount pairing.
        const mapKind = (r: string): RummyRecoveryKind => {
          switch (r) {
            case 'win':             return 'win';
            case 'loss':            return 'loss';
            case 'drop_first':      return 'drop_first';
            case 'drop_middle':     return 'drop_middle';
            case 'kick':            return 'kick';
            case 'invalid_declare': return 'invalid_declare';
            default:                return 'loss';
          }
        };
        const kind = mapKind(String(p.result || 'loss'));
        const amount =
          kind === 'win' ? Number(p.payout || 0) : Number(p.penaltyPoints || 0);
        // Give the WS a beat to deliver a reconnect-sync first — if
        // we land mid-match we don't want the popup on top of the
        // active table.
        setTimeout(() => {
          if (cancelled) return;
          if (phaseRef.current !== 'lobby') return;
          setRecovery({
            sessionId:   Number(p.sessionId),
            kind,
            amount,
            pot:         Number(p.pot || 0),
            platformFee: Number(p.platformFee || 0),
            opponents:   Array.isArray(p.opponents) ? p.opponents : [],
          });
        }, 1200);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  function acknowledgeRecovery() {
    if (!recovery) return;
    const sid = recovery.sessionId;
    markSessionAcked('rummy', sid);
    fetch('/api/rummy/acknowledge-result', {
      method:  'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ sessionId: sid }),
    })
      .then(() => clearSessionAcked('rummy', sid))
      .catch(() => {})
      .finally(() => setRecovery(null));
  }

  // Mount WS.
  useEffect(() => {
    if (!user) return;
    const ws = new RummyWS();
    wsRef.current = ws;
    const offMsg = ws.on((msg) => handleMessage(msg));
    ws.connect();
    return () => {
      offMsg();
      ws.close();
      voiceRef.current?.stop();
      voiceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  function startVoice(mine: string) {
    // NOTE: previously this gated on `config?.audioEnabled`, but
    // `config` is loaded async via `GET /api/rummy/config` and the
    // `rummy_match_start` message frequently arrives BEFORE the
    // fetch resolves — leaving `config` as null and silently
    // aborting voice. Ludo had the same bug and removed the gate
    // for the same reason. The server-side kill-switch
    // (`RummyGame.AUDIO_ENABLED`) still applies: if voice is
    // disabled server-side, `relayVoiceChunk` drops every frame,
    // so a client-side gate adds nothing but a race condition.
    if (!mine) return;
    voiceRef.current?.stop();
    voiceRef.current = null;
    const v = new WSVoice(mine, (m) => { wsRef.current?.send(m); });
    if (micMuted)    v.setMuted(true);
    if (listenMuted) v.setListenMuted(true);
    voiceRef.current = v;
    v.start().then((ok) => {
      if (!ok) {
        toast.push('error', 'Mic blocked — check browser permissions');
      } else {
        toast.push('success', 'Voice chat enabled', 1800);
      }
    });
  }

  function handleMessage(msg: any) {
    if (!msg || typeof msg.type !== 'string') return;
    switch (msg.type) {
      case 'rummy_auth_success':
        // Server confirms auth — flip the bootstrap gate.
        setAuthReady(true);
        break;
      case 'rummy_global_stats':
        if (typeof msg.online_players === 'number') setOnline(msg.online_players);
        if (typeof msg.show_waiting_badge === 'boolean') setShowWaitingBadge(msg.show_waiting_badge);
        if (msg.waiting_by_bet && typeof msg.waiting_by_bet === 'object') {
          setWaitingByBet(msg.waiting_by_bet);
        }
        break;
      case 'rummy_waiting_in_queue':
        setPhase('queue');
        setQueueState({
          current:  Number(msg.current_players  || 0),
          required: Number(msg.required_players || 0),
          profiles: Array.isArray(msg.profiles) ? msg.profiles : [],
          yourUserId: msg.your_user_id || null,
          botActivateAt: typeof msg.bot_activate_at === 'number' ? msg.bot_activate_at : null,
        });
        break;
      case 'wallet_update':
        if (typeof msg.balance === 'number') setWalletBalance(msg.balance);
        break;
      case 'rummy_you_are_seat':
        setMySeat(msg.seat);
        mySeatRef.current = msg.seat;
        break;
      case 'rummy_you_are':
        setMySeat(msg.seat);
        mySeatRef.current = msg.seat;
        if (Array.isArray(msg.hand)) { setHand(msg.hand); setHandGroups([msg.hand]); }
        break;
      case 'rummy_match_start':
        setPhase('playing');
        // Entering an active match — kill any stale previous-game
        // recovery popup so it doesn't resurface when the user lands
        // back in the lobby after this match finishes.
        setRecovery(null);
        setSeats(msg.seats);
        setProfiles(msg.profiles || {});
        setWildCard(msg.wildCard || null);
        setWildRank(msg.wildRank || null);
        setTopDiscard(msg.topDiscard || null);
        setPot(msg.pot || 0);
        setCurrentTurn(msg.turn || null);
        setTurnSeq((n) => n + 1);
        setKicked(new Set()); setDropped(new Set()); setAbsent(new Set());
        setStrikes({});
        setResult(null);
        primeAudio();
        // VS intro plays for ~2.8s, then the deal animation runs for
        // ~1.8s. The backend's FIRST_TURN_GRACE_SECONDS is bumped to
        // 5 so the active player doesn't lose clock time watching
        // the overlay. Shuffle + deal SFX fire at the start of the
        // deal phase so the audio cue matches the card flights.
        setShowVs(true);
        setTimeout(() => {
          setShowVs(false);
          setShowDeal(true);
          playShuffle();
          setTimeout(playDeal, 700);
        }, 2800);
        if (mySeatRef.current) startVoice(mySeatRef.current);
        break;
      case 'rummy_game_resumed':
        setCurrentTurn(msg.turn || null);
        break;
      case 'rummy_reconnect_sync':
        setPhase('playing');
        setSeats(msg.seats);
        setProfiles(msg.profiles || {});
        setWildCard(msg.wildCard || null);
        setWildRank(msg.wildRank || null);
        setTopDiscard(msg.topDiscard || null);
        setPot(msg.pot || 0);
        setCurrentTurn(msg.turn || null);
        setTurnSeq((n) => n + 1);
        setMySeat(msg.your_seat);
        mySeatRef.current = msg.your_seat;
        if (Array.isArray(msg.hand)) { setHand(msg.hand); setHandGroups([msg.hand]); }
        setKicked(new Set(Array.isArray(msg.kicked) ? msg.kicked : []));
        setDropped(new Set(Array.isArray(msg.dropped) ? msg.dropped : []));
        setAbsent(new Set(Array.isArray(msg.absent) ? msg.absent : []));
        setStrikes(msg.strikes && typeof msg.strikes === 'object' ? msg.strikes : {});
        if (mySeatRef.current) startVoice(mySeatRef.current);
        break;
      case 'rummy_turn_update': {
        const prev = currentTurnRef.current;
        setCurrentTurn(msg.turn || null);
        setTurnSeq((n) => n + 1);
        // Warm bell on YOUR turn. Distinct from any other Rummy SFX
        // so the player recognises it instantly (mirrors the Ludo
        // your-turn chime). Only fire on actual transitions into our
        // seat — not on the initial match_start which uses the shuffle
        // / deal sequence as its cue.
        if (msg.turn && msg.turn === mySeatRef.current && msg.turn !== prev) {
          playYourTurnChime();
        }
        break;
      }
      case 'rummy_card_drawn':
        // A successful draw resets that seat's consecutive-timeout count
        // (backend does the same). Without this the dots would stay lit
        // after a player recovers from a couple of misses.
        if (msg.player) setStrikes((s) => ({ ...s, [msg.player]: 0 }));
        if (msg.player === mySeatRef.current && msg.card) {
          setHand((h) => [...h, msg.card]);
          setHandGroups((g) => {
            const last = g.length - 1;
            if (last < 0) return [[msg.card]];
            const next = g.map((grp, i) => (i === last ? [...grp, msg.card] : grp));
            return next;
          });
        } else if (msg.player && msg.player !== mySeatRef.current) {
          // Opponent just drew — flash a card fly-in on their seat so
          // the user sees WHO pulled. Position is looked up via the
          // current seat layout map.
          const flyStyle = seatFlyStyle(msg.player);
          if (flyStyle) spawnFly(flyStyle);
        }
        if (msg.source === 'open') playDrawOpen(); else playDrawClosed();
        break;
      case 'rummy_card_discarded':
        if (msg.player === mySeatRef.current && msg.card) {
          setHand((h) => h.filter((c) => c.id !== msg.card.id));
          setHandGroups((g) => g.map((grp) => grp.filter((c) => c.id !== msg.card.id)).filter((grp) => grp.length > 0));
        } else if (msg.player && msg.player !== mySeatRef.current) {
          // Opponent just discarded — fly-card flashes beside their
          // seat for the same reason as the draw case above.
          const flyStyle = seatFlyStyle(msg.player);
          if (flyStyle) spawnFly(flyStyle);
        }
        setTopDiscard(msg.card || null);
        playDiscard();
        break;
      case 'rummy_deck_reshuffled':
        playShuffle();
        break;
      case 'rummy_player_kicked': {
        const p = msg.player;
        setKicked((prev) => { const n = new Set(prev); n.add(p); return n; });
        setAbsent((prev) => { const n = new Set(prev); n.delete(p); return n; });
        try { voiceRef.current?.dropPeer?.(p); } catch {}
        if (p === mySeatRef.current) {
          setResult({ kind: 'kick', penalty: Number(msg.penalty || 0) });
          setPhase('result');
          playCancelSound();
          playLossTone();
          voiceRef.current?.stop(); voiceRef.current = null;
        }
        break;
      }
      case 'rummy_player_dropped': {
        const p = msg.player;
        setDropped((prev) => { const n = new Set(prev); n.add(p); return n; });
        setAbsent((prev) => { const n = new Set(prev); n.delete(p); return n; });
        try { voiceRef.current?.dropPeer?.(p); } catch {}
        if (p === mySeatRef.current) {
          setResult({ kind: msg.drop === 'middle' ? 'drop_middle' : 'drop_first', penalty: Number(msg.penalty || 0) });
          setPhase('result');
          playLossTone();
          voiceRef.current?.stop(); voiceRef.current = null;
        }
        break;
      }
      case 'rummy_invalid_declare':
        if (msg.player === mySeatRef.current) {
          setResult({ kind: 'invalid_declare', penalty: Number(msg.penalty || 80), reason: msg.reason });
          setPhase('result');
          playInvalid();
          playCancelSound();
        }
        break;
      case 'rummy_player_disconnected':
        setAbsent((prev) => { const n = new Set(prev); n.add(msg.player); return n; });
        try { if (msg.player !== mySeatRef.current) voiceRef.current?.dropPeer?.(msg.player); } catch {}
        break;
      case 'rummy_player_reconnected':
        setAbsent((prev) => { const n = new Set(prev); n.delete(msg.player); return n; });
        break;
      case 'rummy_turn_timeout':
        if (msg.player && typeof msg.strikes === 'number') {
          setStrikes((s) => ({ ...s, [msg.player]: msg.strikes }));
        }
        break;
      case 'rummy_game_over':
        if (msg.winner === mySeatRef.current) {
          setResult({ kind: 'win', payout: Number(msg.payout || 0), pot: Number(msg.pot || 0) });
          playWin(); playChipsClink();
        } else {
          // Find our own score entry for the penalty.
          const me = Array.isArray(msg.scores) ? msg.scores.find((s: any) => s.seat === mySeatRef.current) : null;
          setResult({ kind: 'loss', penalty: Number(me?.penaltyPoints || 0), pot: Number(msg.pot || 0) });
          playLossTone();
        }
        setPhase('result');
        voiceRef.current?.stop(); voiceRef.current = null;
        break;
      case 'rummy_session_ended':
        setResult({ kind: 'aborted' });
        setPhase('result');
        voiceRef.current?.stop(); voiceRef.current = null;
        break;
      case 'rummy_session_aborted':
        toast.push('info', msg.note || 'Match cancelled');
        setPhase('lobby');
        setQueueState(null);
        break;
      case 'rummy_left_game_success':
        setPhase('lobby');
        setQueueState(null);
        voiceRef.current?.stop(); voiceRef.current = null;
        break;
      case 'refund_issued':
        if (typeof msg.amount === 'number') toast.push('info', `Refunded ${msg.amount}`);
        break;
      case 'voice_chunk':
        if (msg.from && typeof msg.pcm === 'string') {
          voiceRef.current?.onRemoteChunk(msg.from, msg.pcm);
        }
        break;
      case 'error':
        if (msg.code === 'insufficient_funds') {
          toast.push('error', 'Not enough balance');
          setPhase('lobby');
          setQueueState(null);
        } else if (msg.code === 'concurrent_match_limit') {
          setConcBlocking(Array.isArray(msg.blocking) ? msg.blocking : []);
          setConcOpen(true);
          setPhase('lobby');
          setQueueState(null);
        } else if (msg.code === 'daily_loss_cap') {
          toast.push('error', 'You\'ve reached your daily loss limit. Try again tomorrow.');
          setPhase('lobby');
          setQueueState(null);
        } else if (msg.code === 'bet_limit') {
          toast.push('error', msg.message || 'This bet is above your limit — contact your agent.');
          setPhase('lobby');
          setQueueState(null);
        } else if (msg.code && !RUMMY_TRANSIENT_ERROR_CODES.has(msg.code)) {
          // Don't toast WS-internal codes like `auth_failed` (the
          // WSManager re-authenticates silently). Surfacing those
          // codes to the user looks like a bug — even though it's
          // handled correctly under the hood.
          toast.push('error', msg.reason || msg.code || 'Action rejected');
        }
        break;
    }
  }

  function handleSelectBet(n: number) {
    if (!config) return;
    setSelectedBet(n);
    if (balance < n) {
      toast.push('error', 'Not enough balance');
      return;
    }
    primeAudio();

    // Pre-warm the mic inside this user gesture — iOS Safari +
    // mobile Chrome silently block `getUserMedia` calls made outside
    // a gesture window, which is why Rummy's voice chat used to stay
    // dead on mobile. `wsVoice.ts` reads `window.__rummyMicStream`
    // in `start()` and attaches the pre-authorised stream without
    // re-prompting. Do NOT gate this on `config?.audioEnabled` —
    // the config may not have loaded yet and the gesture window is
    // gone the moment this function returns. Server-side
    // `AUDIO_ENABLED` still drops frames if voice is off globally.
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then((stream) => { (window as any).__rummyMicStream = stream; })
        .catch((e) => { console.warn('[rummy/voice] mic pre-warm declined:', e); });
    }

    wsRef.current?.send({ type: 'join_queue', bet: n, players: selectedPlayers });
    setPhase('queue');
  }

  function handleCancelQueue() {
    wsRef.current?.send({ type: 'leave_game' });
  }

  function handleDropConfirm() { setDropConfirm(true); }
  function handleDropCancel()  { setDropConfirm(false); }
  function handleDropExecute() {
    setDropConfirm(false);
    wsRef.current?.send({ type: 'leave_game' });
  }

  function handleDiscard(cardId: string) {
    wsRef.current?.send({ type: 'discard', card_id: cardId });
  }
  function handleDeclare(finishCardId: string) {
    // Use the current handGroups arrangement MINUS the finish card.
    const groupIds = handGroups
      .map((g) => g.filter((c) => c.id !== finishCardId).map((c) => c.id))
      .filter((g) => g.length > 0);
    wsRef.current?.send({
      type: 'declare',
      finish_card_id: finishCardId,
      groups: groupIds,
    });
  }
  function handleDrawClosed() { wsRef.current?.send({ type: 'draw_closed' }); }
  function handleDrawOpen()   { wsRef.current?.send({ type: 'draw_open'   }); }

  // Rotate-on-portrait used to short-circuit the render here; that
  // check is now handled by GameRoute in App.tsx, which uses
  // useGameOrientationLock against the GameModule's declared
  // orientation. No inline guard needed any more.

  const yourTurn = currentTurn === mySeat && phase === 'playing';
  const canDeclare = yourTurn && handGroups.flat().length === 14;

  return (
    <div className="rummy-root">
      {/* Loading overlay — covers the route until WS auth AND every
          preloaded asset URL have settled. */}
      <GameLoadingScreen
        visible={!authReady || !assetStatus.ready}
        theme="rummy"
        progress={assetStatus.progress}
      />
      <ConcurrentMatchModal
        open={concOpen}
        blocking={concBlocking}
        walletBalance={balance}
        onResolved={() => {
          setConcOpen(false);
          setConcBlocking(null);
          toast.push('success', 'Active matches resolved — try again', 2000);
        }}
        onCancel={() => setConcOpen(false)}
      />
      <div className="rummy-wood">
        <div className="rummy-felt">
          {phase === 'lobby' && config && (
            <LobbyBanner
              enabled={config.lobbyBanner?.enabled}
              text={config.lobbyBanner?.text}
              tone={config.lobbyBanner?.tone}
            />
          )}
          {phase === 'lobby' && config && (
            <LobbyScreen
              authReady={!!user}
              onlinePlayers={online}
              betTiers={config.betTiers}
              selectedBet={selectedBet}
              selectedPlayers={selectedPlayers}
              platformFeePercent={config.platformFeePercent}
              waitingByBet={waitingByBet}
              showWaitingBadge={showWaitingBadge}
              soundOn={soundOn}
              walletBalance={balance}
              onSelectPlayers={setSelectedPlayers}
              onSelectBet={handleSelectBet}
              onExit={() => nav('/')}
              onOpenSettings={() => setSettingsOpen(true)}
              onAddFunds={() => nav('/wallet')}
            />
          )}

          {/* Previous-game recovery popup — only visible on the lobby
              phase so it never covers an active match reconnect. */}
          {phase === 'lobby' && recovery && (
            <RecoveryScreen
              kind={recovery.kind}
              amount={recovery.amount}
              pot={recovery.pot}
              platformFee={recovery.platformFee}
              opponents={recovery.opponents}
              onAck={acknowledgeRecovery}
            />
          )}

          {phase === 'queue' && queueState && (
            <WaitingModal
              current={queueState.current}
              required={queueState.required}
              bet={selectedBet || 0}
              players={selectedPlayers}
              botActivateAt={queueState.botActivateAt}
              profiles={queueState.profiles}
              yourUserId={queueState.yourUserId}
              onCancel={handleCancelQueue}
            />
          )}

          {phase === 'playing' && (
            <>
              {/* Top UI during play.
                  LEFT   — wallet + pot, stacked so neither overlaps
                           the top-centre opponent seat in 2P/4P/6P.
                  RIGHT  — single Settings.png cog that opens the full
                           settings menu (sound · mic · listen · exit).
                  All three in-game voice / sound toggles previously
                  sat on the right rail; consolidating them into one
                  cog matches Ludo / X&O and frees up seat-adjacent
                  screen real estate. */}
              {/* In-match top bar — same component shapes as Ludo's
                  in-game bar (wallet chip gradient-pill with coin +
                  balance; Settings.png image-only button on the right)
                  styled with Rummy's green felt. The pot chip sits
                  beside the wallet so it never overlaps the top-centre
                  opponent seat. */}
              <div className="rummy-top">
                <div className="flex items-center gap-2 pointer-events-auto">
                  <div
                    className="bg-gradient-to-b from-[#0a3b22] to-[#03190d] rounded-lg shrink-0 flex items-center justify-center pe-[0.7rem] py-[0.3rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)] relative"
                    aria-label={`Balance ${balance}`}
                  >
                    <img src="/games/rummy/Coin.png" alt="" className="w-6 h-6 mr-2 scale-150 relative z-10" />
                    <span className="text-white font-bold text-base tracking-wide">
                      {balance.toLocaleString('en-IN')}
                    </span>
                  </div>
                  {pot > 0 && (
                    <div className="rummy-pot-chip" aria-label={`Pot ${pot}`}>
                      <Icon name="savings" size={14} filled />
                      <span className="rummy-pot-chip__label">POT</span>
                      <span className="rummy-pot-chip__amt">{pot.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { playTap(); setSettingsOpen(true); }}
                  aria-label="Settings"
                  title="Settings"
                  className="w-10 h-10 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform pointer-events-auto"
                >
                  <img src="/games/rummy/Settings.png" alt="" className="w-full h-full object-contain" />
                </button>
              </div>

              {/* Seats for all other players. */}
              {seats.filter((s) => s !== mySeat).map((seat, i) => {
                const pos = seatPositionForIndex(seats.length, i);
                const { side, ...style } = pos as any;
                return (
                  <Seat
                    key={seat}
                    seat={seat}
                    profile={profiles[seat] || {}}
                    side={side}
                    isTurn={currentTurn === seat}
                    absent={absent.has(seat)}
                    out={kicked.has(seat) || dropped.has(seat)}
                    strikes={strikes[seat] || 0}
                    strikeLimit={config?.humanStrikeLimit ?? 3}
                    turnSeq={turnSeq}
                    position={style}
                  />
                );
              })}

              <CenterTable
                topDiscard={topDiscard}
                wildCard={wildCard}
                wildRank={wildRank}
                onDrawClosed={handleDrawClosed}
                onDrawOpen={handleDrawOpen}
              />

              <HandStrip
                groups={handGroups}
                wildRank={wildRank}
                yourTurn={yourTurn}
                onDropToDiscard={handleDiscard}
                onDropToDeclare={handleDeclare}
                onRegroup={setHandGroups}
              />

              {mySeat && (
                <SelfBar
                  name={profiles[mySeat]?.displayName || user?.username || 'You'}
                  avatarUrl={profiles[mySeat]?.avatarUrl || user?.profile_image_url || null}
                  wallet={balance}
                  isTurn={yourTurn}
                  strikes={strikes[mySeat] || 0}
                  strikeLimit={config?.humanStrikeLimit ?? 3}
                  turnSeq={turnSeq}
                />
              )}

              {/* Action buttons. Material Symbol + text pattern so
                  the affordance reads instantly and matches the
                  icon+label pairing used in Ludo/X&O toolbars. */}
              <div className="rummy-actions--left">
                <button
                  className="rummy-action-btn"
                  onClick={() => { playTap(); handleDropConfirm(); }}
                  aria-label="Drop this round"
                >
                  <Icon name="logout" size={14} />
                  <span>DROP</span>
                </button>
              </div>
              <div className="rummy-actions">
                <button
                  className="rummy-action-btn"
                  onClick={() => { playTap(); (window as any).__rummySort?.(); }}
                  aria-label="Sort hand"
                >
                  <Icon name="sort" size={14} />
                  <span>SORT</span>
                </button>
                <button
                  className="rummy-action-btn"
                  onClick={() => { playTap(); (window as any).__rummyGroupSelected?.(); }}
                  aria-label="Group selected cards"
                >
                  <Icon name="dashboard" size={14} />
                  <span>GROUP</span>
                </button>
              </div>

              {yourTurn && (
                <div className="rummy-toast rummy-toast--show">
                  Your turn · draw a card or drop
                </div>
              )}

              {dropConfirm && (
                <div className="rummy-modal-backdrop">
                  <div className="rummy-modal">
                    <h2>Drop this round?</h2>
                    <p>
                      {handGroups.flat().length === 13 && !hasDrawnThisTurn()
                        ? `First drop — penalty ${config?.firstDropPenalty || 20} points.`
                        : `Middle drop — penalty ${config?.middleDropPenalty || 40} points.`}
                      <br/>Your seat will be removed from this match.
                    </p>
                    <div className="flex justify-center gap-3">
                      <button className="px-5 py-2 rounded-full bg-neutral-700 text-white font-bold" onClick={handleDropCancel}>Cancel</button>
                      <button className="px-5 py-2 rounded-full bg-red-600 text-white font-bold" onClick={handleDropExecute}>Yes, Drop</button>
                    </div>
                  </div>
                </div>
              )}

              {canDeclare && (
                <div className="absolute top-[12%] left-1/2 -translate-x-1/2 text-center z-30 pointer-events-none">
                  <div className="inline-block px-3 py-1 rounded-full bg-emerald-700/90 text-[#fde08b] text-[11px] font-black tracking-widest">
                    DRAG YOUR 14TH CARD TO THE DECLARE SLOT
                  </div>
                </div>
              )}

              {/* Opponent draw/discard fly-cards — tiny transient card
                  backs that flash at an opponent's seat when they pull
                  or discard. Each unmounts itself ~650ms after spawn
                  (see spawnFly above). */}
              {flyCards.map((f) => (
                <span
                  key={f.id}
                  className="rummy-fly-card"
                  style={f.style}
                />
              ))}

              {/* Deal animation overlay — shuffles then fans cards out
                  to every seat. Only plays during the first ~1.8s of
                  a fresh match; skipped entirely on reconnect_sync. */}
              {showDeal && (
                <DealAnimation
                  targets={seats.map((seat, _i, arr) => {
                    // `seat === mySeat` ends up at the bottom centre
                    // (behind the HandStrip), opponents around the rim.
                    if (seat === mySeat) {
                      return { seat, bottom: '14%', left: '50%' } as DealTarget;
                    }
                    const opps = arr.filter((s) => s !== mySeat);
                    const relIdx = opps.indexOf(seat);
                    const pos = seatPositionForIndex(arr.length, relIdx) as any;
                    const { side: _s, ...rest } = pos;
                    return { seat, ...rest } as DealTarget;
                  })}
                  onDone={() => setShowDeal(false)}
                />
              )}
            </>
          )}

          {/* VS intro — always on top, fires once per match_start. The
              server clock is already ticking underneath; the backend's
              FIRST_TURN_GRACE_SECONDS covers this visual window. */}
          {showVs && phase === 'playing' && (
            <VsIntro
              mySeat={mySeat}
              seats={seats.map((s) => ({
                seat: s,
                displayName: profiles[s]?.displayName,
                avatarUrl:   profiles[s]?.avatarUrl,
                isBot:       profiles[s]?.isBot,
              }))}
              pot={pot}
              payout={Math.floor(pot * (100 - (config?.platformFeePercent ?? 0)) / 100)}
            />
          )}

          {/* Shared settings modal (see core/components/GameSettingsMenu).
              Opens from both the lobby top-bar and the in-match cog.
              In lobby phase we hide the mic/listen rows and the Exit
              row navigates home; in match phase it triggers the drop
              confirmation so the user can't hard-exit a paid seat by
              accident. */}
          <GameSettingsMenu
            open={settingsOpen}
            soundOn={soundOn}
            micMuted={micMuted}
            listenMuted={listenMuted}
            voiceEnabled={!!config?.audioEnabled && phase === 'playing'}
            onToggleSound={() => { playTap(); setSoundOn((v) => !v); }}
            onToggleMic={() => {
              playTap();
              setMicMuted((v) => { const n = !v; voiceRef.current?.setMuted(n); return n; });
            }}
            onToggleListen={() => {
              playTap();
              setListenMuted((v) => { const n = !v; voiceRef.current?.setListenMuted(n); return n; });
            }}
            onExit={() => {
              playTap();
              setSettingsOpen(false);
              if (phase === 'playing') handleDropConfirm();
              else nav('/');
            }}
            onClose={() => setSettingsOpen(false)}
            exitLabel={phase === 'playing' ? 'Exit Game' : 'Exit to Home'}
            anchor="right"
          />

          {phase === 'result' && result && (
            <div className="rummy-modal-backdrop">
              <div className="rummy-modal">
                {/* Trophy / medal art — mirrors Ludo's win / defeat image
                    pattern for consistent result screens across games. */}
                <img
                  src={result.kind === 'win'
                    ? '/games/rummy/Victory_Trophy.png'
                    : '/games/rummy/Defeat_Medal.png'}
                  alt=""
                  className="mx-auto mb-2"
                  style={{ width: 80, height: 80, objectFit: 'contain' }}
                />
                <h2>{resultTitle(result)}</h2>
                <p>{resultBody(result)}</p>
                <button
                  className="px-6 py-2 rounded-full bg-emerald-700 text-white font-bold"
                  onClick={() => { playTap(); setPhase('lobby'); setResult(null); setQueueState(null); }}
                >
                  Back to Lobby
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function hasDrawnThisTurn() {
    // Rough client-side inference: we hold 14 cards at the moment we
    // drew and haven't discarded yet. Used only to label the drop
    // button's penalty — server is authoritative.
    return handGroups.flat().length > 13;
  }
}

function resultTitle(r: Result): string {
  // Plain-text titles now that the modal renders a trophy/defeat image
  // above them — emoji in the title would fight the artwork for visual
  // weight. Parity with Ludo and X&O result screens.
  switch (r.kind) {
    case 'win':             return 'You Won!';
    case 'loss':            return 'You Lost';
    case 'drop_first':      return 'Dropped';
    case 'drop_middle':     return 'Dropped';
    case 'kick':            return 'Kicked';
    case 'invalid_declare': return 'Invalid Declaration';
    case 'aborted':         return 'Match Aborted';
  }
}

function resultBody(r: Result): string {
  switch (r.kind) {
    case 'win':             return `Payout ${r.payout.toLocaleString('en-IN')}`;
    case 'loss':            return `Penalty ${r.penalty} points`;
    case 'drop_first':      return `First-drop penalty ${r.penalty} points`;
    case 'drop_middle':     return `Middle-drop penalty ${r.penalty} points`;
    case 'kick':            return `Kicked — penalty ${r.penalty} points`;
    case 'invalid_declare': return `${r.reason || 'Invalid sequences'} — penalty ${r.penalty} points`;
    case 'aborted':         return 'The match was aborted. No money was lost.';
  }
}
